/**
 * UploadStep
 *
 * Extracted from LanguageTestModal — Step 3: Upload screenshot
 * with image preview, file info, and analyze button.
 */

import type { TFunction } from 'i18next';
import { useLocale } from '@tonnie/locale';
import { Button, Card } from '@tonnie/ui';
import { AlertTriangle, Image as ImageIcon, Loader2, Upload } from '@tonnie/assets';
import { motion } from 'framer-motion';

import type { ILanguageTestConfig, AnalysisState } from './language-test.types';
import { getFileExtension } from './language-test.types';

export function UploadStep({
  testConfig,
  selectedFile,
  imagePreviewUrl,
  analysis,
  fileInputRef,
  onFileSelect: _onFileSelect,
  onChangeFile,
  onUploadAndAnalyze: _onUploadAndAnalyze,
  t,
}: {
  testConfig: ILanguageTestConfig;
  selectedFile: File | null;
  imagePreviewUrl: string | null;
  analysis: AnalysisState;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeFile: () => void;
  onUploadAndAnalyze: () => void;
  t: TFunction;
}) {
  const { formatFileSize } = useLocale();
  const isProcessing = analysis.status === 'uploading' || analysis.status === 'analyzing';

  return (
    <div className="p-6 space-y-5">
      {!selectedFile ? (
        /* Drop zone */
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-primary/40 rounded-xl p-12 text-center cursor-pointer hover:border-primary/70 hover:bg-primary/5 transition-all duration-200 group"
        >
          <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <Upload className="w-8 h-8 text-primary-accessible" />
          </div>
          <p className="text-sm font-semibold text-foreground mb-1">
            {t('checkup.languageTests.dropHere')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('checkup.languageTests.supportedFormats')}
          </p>
        </div>
      ) : (
        /* Image preview + file info */
        <div className="space-y-4">
          {/* Preview */}
          <Card className="border-2 border-primary/20 overflow-hidden">
            <div className="relative bg-muted/30">
              {imagePreviewUrl && (
                <img
                  src={imagePreviewUrl}
                  alt={t('checkup.languageTests.alt.screenshotPreview')}
                  className="w-full object-contain max-h-[340px]"
                />
              )}
              {isProcessing && (
                <div className="absolute inset-0 bg-background/60 backdrop-blur-sm flex items-center justify-center">
                  <div className="text-center space-y-3">
                    <Loader2 className="w-8 h-8 text-primary-accessible animate-spin mx-auto" />
                    <p className="text-sm font-medium text-foreground">
                      {analysis.status === 'uploading'
                        ? t('checkup.languageTests.uploading')
                        : t('checkup.languageTests.analyzing')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* File info row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center shrink-0">
                <ImageIcon className="w-5 h-5 text-primary-accessible" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground">
                  {getFileExtension(selectedFile.name)} &bull; {formatFileSize(selectedFile.size)}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onChangeFile}
              disabled={isProcessing}
              className="text-xs shrink-0"
            >
              {t('checkup.languageTests.changeFile')}
            </Button>
          </div>

          {/* Error state */}
          {analysis.status === 'upload_error' && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg"
            >
              <AlertTriangle className="w-4 h-4 text-destructive-accessible shrink-0" />
              <span className="text-sm text-destructive-accessible">
                {t('checkup.languageTests.uploadFailedError')}
              </span>
            </motion.div>
          )}
        </div>
      )}

      {/* Sample reference */}
      {testConfig.sampleImagePath && (
        <div className="border border-border/50 rounded-lg p-3 flex items-start gap-3">
          <img
            src={
              testConfig.sampleImagePath.startsWith('/')
                ? testConfig.sampleImagePath
                : `/${testConfig.sampleImagePath}`
            }
            alt={t('checkup.languageTests.alt.reference')}
            className="w-16 h-12 object-cover rounded border border-border"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          <div>
            <span className="text-xs font-medium text-foreground block">
              {t('checkup.languageTests.referenceImage')}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('checkup.languageTests.referenceHint')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
